import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAccount, useSignTypedData } from "wagmi";
import { EmptyState, ErrorState, Money, MktButton, SectionHeader, StatePill, Tag } from "./primitives";
import { fmtMicro, shortAddr } from "./mkt-format";
import { useMine, useMarketplaceConfig } from "./useMarketplace";
import { useSession } from "./useSession";
import { goMarketplace } from "./mkt-routing";
import { marketplaceApi, MarketplaceError, type ArtworkListing, type Auction, type CompletedSettlement, type Listing, type MarketplaceConfig, type Offer } from "../../lib/marketplace-api";
import { buildOrderTypedData } from "./order-eip712";
import { symbolFor } from "./mkt-format";
import { useT } from "../../i18n";
import { SellLandParcelPicker } from "./SellLandParcelPicker";
import { SellLandWizard, type SellSourceParcel } from "./SellLandWizard";
import { SellArtworkModal } from "./SellArtworkModal";

function assetLabel(assetKey: string): { label: string; isArtwork: boolean; artwork?: string; parcelId?: string } {
  if (assetKey.startsWith("artwork:")) return { label: "", isArtwork: true, artwork: assetKey.slice("artwork:".length) };
  const parts = assetKey.split(":");
  return { label: "", isArtwork: false, parcelId: parts[parts.length - 1] };
}

export function MyMarketplaceTab() {
  const session = useSession();
  const { data: config } = useMarketplaceConfig();
  const mineQuery = useMine(session.isAuthenticated);
  const qc = useQueryClient();
  const t = useT();
  const { address } = useAccount();
  const { signTypedDataAsync } = useSignTypedData();
  const [pickerOpen, setPickerOpen] = useState(false);
  const [sellArtworkOpen, setSellArtworkOpen] = useState(false);
  const [sellParcel, setSellParcel] = useState<SellSourceParcel | null>(null);
  const [busyOffer, setBusyOffer] = useState<string | null>(null);
  const [offerErr, setOfferErr] = useState<string | null>(null);

  const refresh = () => qc.invalidateQueries({ queryKey: ["mkt", "mine"] });

  const cancel = useMutation({
    mutationFn: (orderHash: string) => marketplaceApi.cancelListing(orderHash),
    onSuccess: () => { refresh(); qc.invalidateQueries({ queryKey: ["mkt", "listings"] }); },
  });
  const withdraw = useMutation({
    mutationFn: (offerHash: string) => marketplaceApi.withdrawOffer(offerHash),
    onSuccess: refresh,
  });
  const cancelArtwork = useMutation({
    mutationFn: (artworkId: string) => marketplaceApi.cancelArtworkListing(artworkId),
    onSuccess: () => { refresh(); qc.invalidateQueries({ queryKey: ["mkt", "artworks"] }); },
  });

  const onAcceptOffer = async (o: Offer) => {
    if (!config?.marketplace_address || !address) return;
    const parts = o.asset_key.split(":");
    if (parts.length !== 4 || parts[0] !== "land") {
      setOfferErr(t("marketplace.mine.acceptUnsupported"));
      return;
    }
    const collection = parts[2];
    const tokenId = parts[3];
    setOfferErr(null);
    setBusyOffer(o.offer_hash);
    try {
      const nonce = Date.now();
      const expiry = Math.floor(Date.now() / 1000) + 30 * 86400;
      const typed = buildOrderTypedData({
        chainId: config.chain_id,
        verifyingContract: config.marketplace_address as `0x${string}`,
        order: {
          seller: address as `0x${string}`,
          collection: collection as `0x${string}`,
          tokenId: BigInt(tokenId),
          currency: o.currency as `0x${string}`,
          price: BigInt(o.amount_micro),
          listingVersion: 0n,
          nonce: BigInt(nonce),
          expiryRealTs: BigInt(expiry),
        },
      });
      const signature = await signTypedDataAsync(typed);
      await marketplaceApi.acceptOffer(o.offer_hash, {
        seller_signature: signature, nonce, listing_version: 0, expiry_real_ts: expiry,
      });
      refresh();
      qc.invalidateQueries({ queryKey: ["mkt", "listings"] });
    } catch (e) {
      setOfferErr(e instanceof MarketplaceError ? e.message : e instanceof Error ? e.message : String(e));
    } finally {
      setBusyOffer(null);
    }
  };

  const onDeclineOffer = async (o: Offer) => {
    setOfferErr(null);
    setBusyOffer(o.offer_hash);
    try {
      await marketplaceApi.declineOffer(o.offer_hash);
      refresh();
    } catch (e) {
      setOfferErr(e instanceof MarketplaceError ? e.message : e instanceof Error ? e.message : String(e));
    } finally {
      setBusyOffer(null);
    }
  };

  if (session.isLoading) {
    return <div data-testid="mine-loading" className="py-24 text-center font-mono text-[11px] tracking-[0.2em] uppercase text-text-muted animate-pulse">{t("marketplace.states.loading")}</div>;
  }
  if (!session.isAuthenticated) {
    return (
      <EmptyState
        testId="mine-signin"
        title={t("marketplace.mine.signInTitle")}
        hint={t("marketplace.mine.signInHint")}
        icon={<a href="#auth" className="font-mono text-[11px] text-neon-teal underline">{t("marketplace.mine.connectSignIn")}</a>}
      />
    );
  }
  if (mineQuery.isError) {
    return <ErrorState title={t("marketplace.mine.loadErrorTitle")} onRetry={() => mineQuery.refetch()} testId="mine-error" />;
  }
  const mine = mineQuery.data;
  if (!mine) {
    return <div data-testid="mine-loading" className="py-24 text-center font-mono text-[11px] tracking-[0.2em] uppercase text-text-muted animate-pulse">{t("marketplace.mine.loadingYours")}</div>;
  }

  return (
    <div data-testid="marketplace-mine-tab" className="space-y-12">
      <section data-testid="mine-listings">
        <SectionHeader index="01" title={t("marketplace.mine.listings")}
          action={
            <div className="flex items-center gap-3">
              <span className="font-mono text-[10.5px] uppercase tracking-[0.14em] text-text-muted">{mine.counts.listings}</span>
              {config?.enabled && config?.marketplace_enabled && (
                <MktButton variant="coral" testId="mine-list-land" onClick={() => setPickerOpen(true)}>
                  {t("marketplace.sell.listLand")}
                </MktButton>
              )}
              {config?.artwork_enabled && (
                <MktButton variant="primary" testId="mine-sell-artwork" onClick={() => setSellArtworkOpen(true)}>
                  {t("marketplace.artworkMkt.sellArtwork")}
                </MktButton>
              )}
            </div>
          } />
        {mine.listings.length === 0 ? (
          <EmptyState title={t("marketplace.mine.noListings")} hint={t("marketplace.mine.noListingsHint")} testId="mine-listings-empty" />
        ) : (
          <div className="neon-glass rounded-lg border border-border-divider divide-y divide-border-divider">
            {mine.listings.map((l) => (
              <div key={l.order_hash} data-testid="mine-listing-row" className="flex items-center justify-between gap-3 px-4 py-3">
                <button type="button" onClick={() => goMarketplace("land", {}, { kind: "land", id: l.order_hash })}
                  className="flex items-center gap-2 font-display text-sm text-text-primary hover:text-neon-teal transition-colors">
                  {t("marketplace.common.parcelNumber", { id: String(l.parent_token_id ?? l.token_id) })}
                  {l.parent_token_id != null && (l.cells?.length ?? 0) > 0 && (
                    <span className="font-mono text-[10px] text-text-muted">· {t("marketplace.sell.pickCells", { n: l.cells.length })}</span>
                  )}
                  {l.preview_demo && <Tag tone="coral" testId="mine-listing-demo-badge">{t("marketplace.sell.previewDemo")}</Tag>}
                </button>
                <div className="flex items-center gap-4">
                  <Money amount={fmtMicro(l.price_micro)} symbol={symbolFor(l.currency, config)} className="text-sm text-neon-teal" />
                  <StatePill state={l.status} />
                  <MktButton variant="ghost" testId="mine-cancel-listing" onClick={() => cancel.mutate(l.order_hash)} disabled={cancel.isPending}>{t("marketplace.mine.cancel")}</MktButton>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <section data-testid="mine-artwork-listings">
        <SectionHeader index="01b" title={t("marketplace.artworkMkt.myArtworkListings")}
          action={<span className="font-mono text-[10.5px] uppercase tracking-[0.14em] text-neon-teal">{mine.counts.artwork_listings ?? mine.artwork_listings?.length ?? 0}</span>} />
        <ArtworkListingList items={mine.artwork_listings ?? []} config={config}
          onCancel={(id) => cancelArtwork.mutate(id)} cancelling={cancelArtwork.isPending} />
      </section>

      <section data-testid="mine-auctions">
        <SectionHeader index="01c" title={t("marketplace.artworkMkt.myAuctions")}
          action={<span className="font-mono text-[10.5px] uppercase tracking-[0.14em] text-neon-coral">{mine.counts.my_auctions ?? mine.my_auctions?.length ?? 0}</span>} />
        <MyAuctionList items={mine.my_auctions ?? []} config={config} marketNow={mine.market_now} />
      </section>

      <section data-testid="mine-offers-received">
        <SectionHeader index="02" title={t("marketplace.mine.offersReceived")}
          action={<span className="font-mono text-[10.5px] uppercase tracking-[0.14em] text-neon-coral">{mine.counts.offers_received}</span>} />
        {offerErr && (
          <p data-testid="mine-offer-error" className="mb-2 font-mono text-[10.5px] text-neon-coral">{offerErr}</p>
        )}
        <OfferList
          offers={mine.offers_received}
          config={config}
          kind="received"
          onAccept={onAcceptOffer}
          onDecline={onDeclineOffer}
          busyOfferHash={busyOffer}
        />
      </section>

      <section data-testid="mine-pending-purchases">
        <SectionHeader index="02b" title={t("marketplace.mine.pendingPurchases")}
          action={<span className="font-mono text-[10.5px] uppercase tracking-[0.14em] text-neon-teal">{mine.counts.pending_purchases ?? mine.pending_purchases?.length ?? 0}</span>} />
        <PendingPurchaseList items={mine.pending_purchases ?? []} config={config} />
      </section>

      <section data-testid="mine-offers-made">
        <SectionHeader index="03" title={t("marketplace.mine.offersMade")}
          action={<span className="font-mono text-[10.5px] uppercase tracking-[0.14em] text-text-muted">{mine.counts.offers_made}</span>} />
        <OfferList offers={mine.offers_made} config={config} kind="made" onWithdraw={(h) => withdraw.mutate(h)} withdrawing={withdraw.isPending} />
      </section>

      <section data-testid="mine-bids">
        <SectionHeader index="04" title={t("marketplace.mine.bids")}
          action={<span className="font-mono text-[10.5px] uppercase tracking-[0.14em] text-text-muted">{mine.counts.bids}</span>} />
        {mine.bids.length === 0 ? (
          <EmptyState title={t("marketplace.mine.noBids")} hint={t("marketplace.mine.noBidsHint")} testId="mine-bids-empty" />
        ) : (
          <div className="neon-glass rounded-lg border border-border-divider divide-y divide-border-divider">
            {mine.bids.map((b) => (
              <div key={b.auction_id} data-testid="mine-bid-row" className="flex items-center justify-between gap-3 px-4 py-3">
                <span className="font-display text-sm text-text-primary">{b.artwork_title ?? t("marketplace.mine.auction")}</span>
                <div className="flex items-center gap-4">
                  <span className="font-mono text-[10.5px] text-text-muted">{t("marketplace.mine.bidPrefix")} {fmtMicro(b.amount_micro)}</span>
                  <StatePill state={b.leading ? "open" : "confirming"} label={b.leading ? t("marketplace.mine.leading") : t("marketplace.mine.outbid")} />
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <section data-testid="mine-sales">
        <SectionHeader index="05" title={t("marketplace.mine.sales")}
          action={<span className="font-mono text-[10.5px] uppercase tracking-[0.14em] text-neon-teal">{mine.counts.sales}</span>} />
        <SettlementList items={mine.sales} config={config} kind="sale" />
      </section>

      <section data-testid="mine-purchases">
        <SectionHeader index="06" title={t("marketplace.mine.purchases")}
          action={<span className="font-mono text-[10.5px] uppercase tracking-[0.14em] text-text-muted">{mine.counts.purchases}</span>} />
        <SettlementList items={mine.purchases} config={config} kind="purchase" />
      </section>

      <SellLandParcelPicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onPick={(p) => {
          setPickerOpen(false);
          setSellParcel(p);
        }}
      />
      <SellLandWizard
        key={sellParcel?.parcel_id ?? "none"}
        open={!!sellParcel}
        sourceParcel={sellParcel}
        onClose={() => setSellParcel(null)}
      />
      {sellArtworkOpen && (
        <SellArtworkModal open onClose={() => setSellArtworkOpen(false)} onDone={refresh} />
      )}
    </div>
  );
}

function OfferList({
  offers, config, kind, onWithdraw, withdrawing, onAccept, onDecline, busyOfferHash,
}: {
  offers: Offer[]; config?: MarketplaceConfig | null; kind: "made" | "received";
  onWithdraw?: (offerHash: string) => void; withdrawing?: boolean;
  onAccept?: (offer: Offer) => void; onDecline?: (offer: Offer) => void;
  busyOfferHash?: string | null;
}) {
  const t = useT();
  if (offers.length === 0) {
    return <EmptyState title={kind === "made" ? t("marketplace.mine.noOpenOffers") : t("marketplace.mine.noOffersReceived")}
      hint={kind === "made" ? t("marketplace.mine.offerMadeHint") : t("marketplace.mine.offerReceivedHint")} testId={`mine-offers-${kind}-empty`} />;
  }
  return (
    <div className="neon-glass rounded-lg border border-border-divider divide-y divide-border-divider">
      {offers.map((o) => {
        const a = assetLabel(o.asset_key);
        const label = a.isArtwork ? t("marketplace.mine.artworkLabel") : t("marketplace.common.parcelNumber", { id: String(a.parcelId) });
        const busy = busyOfferHash === o.offer_hash;
        return (
          <div key={o.offer_hash} data-testid={`mine-offer-${kind}-row`} className="flex items-center justify-between gap-3 px-4 py-3">
            <button type="button"
              onClick={() => a.artwork ? goMarketplace("artworks", {}, { kind: "artwork", id: a.artwork }) : undefined}
              className="font-display text-sm text-text-primary hover:text-neon-teal transition-colors text-left">
              {label}
              <span className="ml-2 font-mono text-[10px] text-text-muted">{shortAddr(o.offeror)}</span>
            </button>
            <div className="flex items-center gap-4">
              <Money amount={fmtMicro(o.amount_micro)} symbol={symbolFor(o.currency, config)} className="text-sm text-text-primary" />
              {kind === "made" && onWithdraw && (
                <MktButton variant="ghost" testId="mine-withdraw-offer" onClick={() => onWithdraw(o.offer_hash)} disabled={withdrawing}>{t("marketplace.mine.withdraw")}</MktButton>
              )}
              {kind === "received" && !a.isArtwork && (onAccept || onDecline) && (
                <>
                  {onAccept && (
                    <MktButton variant="coral" testId="mine-accept-offer" onClick={() => onAccept(o)} disabled={busy}>
                      {busy ? t("marketplace.mine.accepting") : t("marketplace.mine.accept")}
                    </MktButton>
                  )}
                  {onDecline && (
                    <MktButton variant="ghost" testId="mine-decline-offer" onClick={() => onDecline(o)} disabled={busy}>
                      {t("marketplace.mine.decline")}
                    </MktButton>
                  )}
                </>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function PendingPurchaseList({ items, config }: { items: Listing[]; config?: MarketplaceConfig | null }) {
  const t = useT();
  if (items.length === 0) {
    return <EmptyState title={t("marketplace.mine.noPendingPurchases")} hint={t("marketplace.mine.pendingPurchasesHint")} testId="mine-pending-purchases-empty" />;
  }
  return (
    <div className="neon-glass rounded-lg border border-border-divider divide-y divide-border-divider">
      {items.map((l) => (
        <div key={l.order_hash} data-testid="mine-pending-purchase-row" className="flex items-center justify-between gap-3 px-4 py-3">
          <span className="font-display text-sm text-text-primary">
            {t("marketplace.common.parcelNumber", { id: String(l.parent_token_id ?? l.token_id) })}
          </span>
          <div className="flex items-center gap-4">
            <Money amount={fmtMicro(l.price_micro)} symbol={symbolFor(l.currency, config)} className="text-sm text-neon-teal" />
            <MktButton variant="coral" testId="mine-complete-purchase"
              onClick={() => goMarketplace("land", {}, { kind: "land", id: l.order_hash })}>
              {t("marketplace.mine.completePurchase")}
            </MktButton>
          </div>
        </div>
      ))}
    </div>
  );
}

function fmtDate(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  return isNaN(d.getTime()) ? "" : d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

function SettlementList({
  items, config, kind,
}: {
  items: CompletedSettlement[]; config?: MarketplaceConfig | null; kind: "sale" | "purchase";
}) {
  const t = useT();
  if (items.length === 0) {
    return (
      <EmptyState
        testId={`mine-${kind}s-empty`}
        title={kind === "sale" ? t("marketplace.mine.noSales") : t("marketplace.mine.noPurchases")}
        hint={kind === "sale" ? t("marketplace.mine.noSalesHint") : t("marketplace.mine.noPurchasesHint")}
      />
    );
  }
  return (
    <div className="neon-glass rounded-lg border border-border-divider divide-y divide-border-divider">
      {items.map((s) => {
        const counterparty = kind === "sale" ? s.buyer : s.seller;
        const sym = symbolFor(s.currency ?? "", config);
        return (
          <div key={s.settlement_id} data-testid={`mine-${kind}-row`}
            className="flex flex-col gap-2 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex flex-col gap-1">
              <button type="button"
                onClick={() => goMarketplace("land", {}, { kind: "land", id: s.settlement_id })}
                className="text-left font-display text-sm text-text-primary hover:text-neon-teal transition-colors">
                {t("marketplace.common.parcelNumber", { id: String(s.token_id ?? "?") })}
              </button>
              <span className="font-mono text-[10px] text-text-muted">
                {(kind === "sale" ? t("marketplace.mine.soldTo") : t("marketplace.mine.boughtFrom"))} {shortAddr(counterparty)}
                {s.created_at ? ` · ${fmtDate(s.created_at)}` : ""}
              </span>
            </div>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
              <Money amount={fmtMicro(s.gross_micro)} symbol={sym} className="text-sm text-neon-teal" testId={`mine-${kind}-price`} />
              {kind === "sale" && (
                <>
                  <span className="font-mono text-[10px] text-text-muted">{t("marketplace.mine.feeShort")} {fmtMicro(s.grid_fee_micro)}</span>
                  <span className="font-mono text-[10px] text-text-primary">{t("marketplace.mine.netShort")} {fmtMicro(s.seller_proceeds_micro)} {sym}</span>
                </>
              )}
              <StatePill state={s.status} label={t("marketplace.mine.soldStatus")} testId={`mine-${kind}-status`} />
              <a href={`https://sepolia.basescan.org/tx/${s.tx_hash}`} target="_blank" rel="noreferrer"
                data-testid={`mine-${kind}-tx`}
                className="font-mono text-[10px] text-neon-teal hover:underline">
                {t("marketplace.mine.viewTx")}
              </a>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function ArtworkListingList({
  items, config, onCancel, cancelling,
}: {
  items: ArtworkListing[]; config?: MarketplaceConfig | null;
  onCancel: (artworkId: string) => void; cancelling: boolean;
}) {
  const t = useT();
  if (items.length === 0) {
    return <EmptyState title={t("marketplace.artworkMkt.noArtworkListings")} hint={t("marketplace.artworkMkt.noArtworkListingsHint")} testId="mine-artwork-listings-empty" />;
  }
  return (
    <div className="neon-glass rounded-lg border border-border-divider divide-y divide-border-divider">
      {items.map((l) => (
        <div key={l.artwork_id} data-testid="mine-artwork-listing-row" className="flex items-center justify-between gap-3 px-4 py-3">
          <button type="button"
            onClick={() => goMarketplace("artworks", {}, { kind: "artwork", id: l.artwork_id })}
            className="font-display text-sm text-text-primary hover:text-neon-teal transition-colors text-left">
            {t("marketplace.mine.artworkLabel")}
            <span className="ml-2 font-mono text-[10px] text-text-muted">{shortAddr(l.artwork_id)}</span>
          </button>
          <div className="flex items-center gap-4">
            <Money amount={fmtMicro(l.price_micro)} symbol={symbolFor(l.currency, config)} className="text-sm text-neon-teal" />
            <StatePill state="active" />
            <MktButton variant="ghost" testId="mine-cancel-artwork-listing" onClick={() => onCancel(l.artwork_id)} disabled={cancelling}>
              {t("marketplace.mine.cancel")}
            </MktButton>
          </div>
        </div>
      ))}
    </div>
  );
}

function MyAuctionList({
  items, config, marketNow,
}: {
  items: Auction[]; config?: MarketplaceConfig | null; marketNow: number;
}) {
  const t = useT();
  if (items.length === 0) {
    return <EmptyState title={t("marketplace.artworkMkt.noMyAuctions")} hint={t("marketplace.artworkMkt.noMyAuctionsHint")} testId="mine-auctions-empty" />;
  }
  return (
    <div className="neon-glass rounded-lg border border-border-divider divide-y divide-border-divider">
      {items.map((a) => {
        const hasBid = !!a.highest_bidder;
        const current = hasBid ? a.highest_bid_micro : a.reserve_micro;
        const ended = a.end_market_ts - marketNow <= 0;
        return (
          <div key={a.auction_id} data-testid="mine-auction-row" className="flex items-center justify-between gap-3 px-4 py-3">
            <button type="button"
              onClick={() => goMarketplace("artworks", {}, { kind: "artwork", id: a.artwork_id })}
              className="font-display text-sm text-text-primary hover:text-neon-teal transition-colors text-left">
              {a.artwork_title ?? t("marketplace.mine.auction")}
            </button>
            <div className="flex items-center gap-4">
              <Money amount={fmtMicro(current)} symbol={symbolFor(a.currency, config)} className="text-sm text-neon-coral" />
              <StatePill state={ended ? "confirming" : "open"} label={ended ? t("marketplace.clock.ended") : undefined} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

